/**
 * Derivacoes PURAS a partir do detalhe de um lead: situacao, contatos que
 * contam, ultimo contato, proximo toque, plano da regua, janela de credito,
 * a frase executiva do topo e as sugestoes de acao.
 *
 * Mora fora dos componentes por dois motivos:
 *  1. O gerador simulado (mock.ts) usa AS MESMAS funcoes para preencher a
 *     lista (`LeadListRow`): a situacao que aparece na tabela e a que aparece
 *     no detalhe sao a mesma conta, entao nunca divergem.
 *  2. Quando o backend passar a devolver esses campos, a tela ja le do
 *     servidor e estas funcoes viram so a verificacao da previa.
 *
 * Regras de negocio embutidas (ADR-001, decididas pelo dono em 24/09/2026):
 *  - "Contato" = tentativa que chegou ao telefone: ligacao atendida, caixa
 *    postal ou sem resposta; mensagem ENVIADA. `failed` e `pending` nao contam.
 *  - A janela de credito e de 7 dias a partir do ULTIMO contato do MOTOR.
 *    A vendedora assumir a conversa nao abre nem reabre a janela: ela e
 *    "fechado por", o credito e do toque (ADR-001 E5, 25/09/2026).
 *  - Pagamento antes de qualquer contato aborta a regua sem comissao.
 *  - Toda regua parte do fato (abandono ou vencimento). Nenhum toque existe
 *    antes dele; o plano (`planoDaRegua`) nasce em T0 e so anda para frente.
 */

import { formatBRLFromCents, formatDateTime } from '../../lib/format';
import type { FunnelKind, RefundPolicy } from '../../types';
import type {
  ContactChannel,
  DegrauDaRegua,
  DesfechoDoContato,
  LeadDetail,
  ProximoToque,
  SituacaoDoLead,
  TimelineEvent,
  UltimoContato,
} from './types';
import { CANAL, DESFECHO_LABEL, descreverRelativo, FATO, prazoRestante, SITUACAO } from './vocabulario';
import { aoContato } from '../comercial/vendaAssistida';

export const JANELA_DE_CREDITO_DIAS = 7;
const MIN_MS = 60_000;
const DIA_MS = 86_400_000;

export interface EstadoLocalDoLead {
  readonly pausada: boolean;
  readonly removida: boolean;
  readonly vendaMarcada: boolean;
  /** O que a atribuicao respondeu para a venda assistida marcada (ausente = sem venda local). */
  readonly vendaDesfecho?: DesfechoDaVendaLocal;
}

export type DesfechoDaVendaLocal = 'creditado' | 'creditado_revertido' | 'sem_contato';

export const SEM_ESTADO_LOCAL: EstadoLocalDoLead = { pausada: false, removida: false, vendaMarcada: false };

function ordenados(timeline: readonly TimelineEvent[]): readonly TimelineEvent[] {
  return [...timeline].sort((a, b) => a.at.localeCompare(b.at));
}

export function ultimoEvento(timeline: readonly TimelineEvent[]): TimelineEvent | null {
  const lista = ordenados(timeline);
  return lista[lista.length - 1] ?? null;
}

// -----------------------------------------------------------------------------
// Situacao
// -----------------------------------------------------------------------------

export function situacaoDoLead(lead: LeadDetail, local: EstadoLocalDoLead = SEM_ESTADO_LOCAL): SituacaoDoLead {
  if (local.removida) {
    return 'cancelado';
  }
  if (local.vendaMarcada) {
    // Venda sem toque na janela nao e "recuperado" (que promete comissao):
    // no dinheiro, e igual a quem pagou sem a regua.
    return local.vendaDesfecho === 'sem_contato' ? 'pagou_sozinho' : local.vendaDesfecho === 'creditado_revertido' ? 'cancelado' : 'recuperado';
  }
  const conversaoAtiva = lead.conversion !== null && !lead.conversion.reversed;
  if (conversaoAtiva || lead.jobStatus === 'succeeded') {
    return 'recuperado';
  }
  if (lead.conversion !== null && lead.conversion.reversed) {
    return 'cancelado';
  }
  if (lead.recoveredAt !== null) {
    return 'pagou_sozinho';
  }
  const ultimo = ultimoEvento(lead.timeline);
  if (ultimo?.kind === 'seller_took_over') {
    return 'com_comercial';
  }
  if (lead.timeline.some((e) => e.kind === 'rule_ended' && e.reason === 'descadastrou')) {
    return 'cancelado';
  }
  switch (lead.jobStatus) {
    case 'pending':
      return 'na_fila';
    case 'running':
      return 'em_recuperacao';
    case 'aborted':
      return 'cancelado';
    case 'failed':
      return lead.timeline.some((e) => e.kind === 'rule_ended') || lead.tier === 'exhausted' ? 'esgotado' : 'falhou';
    default: {
      const nunca: never = lead.jobStatus;
      return nunca;
    }
  }
}

// -----------------------------------------------------------------------------
// Contatos
// -----------------------------------------------------------------------------

export interface ContatoFeito {
  readonly channel: ContactChannel;
  readonly at: string;
  readonly touchLabel: string;
  readonly outcome: DesfechoDoContato;
  readonly eventId: string;
}

/** Tentativas que CHEGARAM ao telefone (ADR-001). E o que abre a janela de 7 dias. */
export function contatosQueContam(timeline: readonly TimelineEvent[]): readonly ContatoFeito[] {
  const lista: ContatoFeito[] = [];
  for (const e of ordenados(timeline)) {
    if (e.kind === 'voice_call' && e.status !== 'failed') {
      lista.push({ channel: 'voice', at: e.at, touchLabel: e.touchLabel, outcome: e.status, eventId: e.id });
    } else if (e.kind === 'whatsapp' && e.stage === 'sent') {
      lista.push({ channel: 'whatsapp', at: e.at, touchLabel: e.touchLabel, outcome: 'sent', eventId: e.id });
    } else if (e.kind === 'email' && e.stage === 'sent') {
      lista.push({ channel: 'email', at: e.at, touchLabel: e.touchLabel, outcome: 'sent', eventId: e.id });
    }
  }
  return lista;
}

export function contaComoContato(event: TimelineEvent): boolean {
  return (event.kind === 'voice_call' && event.status !== 'failed') || (event.kind === 'whatsapp' && event.stage === 'sent') || (event.kind === 'email' && event.stage === 'sent');
}

/**
 * Por que um pagamento ficou sem comissao. "Pagou sozinho" cobria dois casos
 * diferentes e a tela dizia "antes do 1o toque" para quem recebeu toques e
 * pagou depois dos 7 dias: o lojista via "nenhum toque enviado" num lead
 * com tres ligacoes.
 *  - antes_do_primeiro_toque: nenhum contato que conta ate o pagamento.
 *  - fora_da_janela: houve contato, mas o ultimo antes do pagamento tem mais de 7 dias.
 *  - sem_credito: houve contato dentro dos 7 dias e mesmo assim nao ha conversao
 *    (quem decide e o servidor; a tela nao inventa o motivo).
 */
export type PagamentoSemCredito =
  | { readonly kind: 'antes_do_primeiro_toque' }
  | { readonly kind: 'fora_da_janela'; readonly diasDepoisDoUltimo: number }
  | { readonly kind: 'sem_credito' };

export function pagamentoSemCredito(lead: LeadDetail): PagamentoSemCredito {
  const pagoMs = lead.recoveredAt === null ? Number.POSITIVE_INFINITY : new Date(lead.recoveredAt).getTime();
  const antes = contatosQueContam(lead.timeline).filter((c) => new Date(c.at).getTime() <= pagoMs);
  const ultimo = antes[antes.length - 1];
  if (ultimo === undefined) {
    return { kind: 'antes_do_primeiro_toque' };
  }
  if (lead.recoveredAt === null) {
    return { kind: 'sem_credito' };
  }
  const distancia = pagoMs - new Date(ultimo.at).getTime();
  if (distancia > JANELA_DE_CREDITO_DIAS * DIA_MS) {
    return { kind: 'fora_da_janela', diasDepoisDoUltimo: Math.floor(distancia / DIA_MS) };
  }
  return { kind: 'sem_credito' };
}

export interface ToquesRecebidos {
  readonly ligacoes: number;
  readonly whatsapps: number;
  readonly emails: number;
  readonly total: number;
}

export function toquesRecebidos(timeline: readonly TimelineEvent[]): ToquesRecebidos {
  let ligacoes = 0;
  let whatsapps = 0;
  let emails = 0;
  for (const c of contatosQueContam(timeline)) {
    if (c.channel === 'voice') {
      ligacoes += 1;
    } else if (c.channel === 'whatsapp') {
      whatsapps += 1;
    } else if (c.channel === 'email') {
      emails += 1;
    }
  }
  return { ligacoes, whatsapps, emails, total: ligacoes + whatsapps + emails };
}

/**
 * A interacao mais recente com a pessoa, em qualquer sentido: o estagio mais
 * novo de um toque (lido, clicou), a resposta do lead, a vendedora assumindo.
 * E o que a coluna "Ultimo contato" mostra.
 */
export function ultimoContato(timeline: readonly TimelineEvent[]): UltimoContato | null {
  let ultimo: UltimoContato | null = null;
  let ultimoToque = '';
  for (const e of ordenados(timeline)) {
    switch (e.kind) {
      case 'voice_call':
        ultimoToque = e.touchLabel;
        ultimo = { channel: 'voice', at: e.at, outcome: e.status, touchLabel: e.touchLabel };
        break;
      case 'whatsapp':
        ultimoToque = e.touchLabel;
        ultimo = { channel: 'whatsapp', at: e.at, outcome: e.stage, touchLabel: e.touchLabel };
        break;
      case 'email':
        ultimoToque = e.touchLabel;
        ultimo = { channel: 'email', at: e.at, outcome: e.stage, touchLabel: e.touchLabel };
        break;
      case 'lead_reply':
        ultimo = { channel: e.channel, at: e.at, outcome: 'replied', touchLabel: ultimoToque };
        break;
      case 'seller_took_over':
        ultimo = { channel: 'seller', at: e.at, outcome: 'took_over', touchLabel: ultimoToque };
        break;
      case 'seller_sale':
        ultimo = { channel: 'seller', at: e.at, outcome: 'sale', touchLabel: ultimoToque };
        break;
      default:
        break;
    }
  }
  return ultimo;
}

export function proximoToque(lead: LeadDetail, local: EstadoLocalDoLead = SEM_ESTADO_LOCAL): ProximoToque | null {
  const situacao = situacaoDoLead(lead, local);
  if (!SITUACAO[situacao].viva || local.pausada || lead.rule.paused || lead.rule.nextTouch === null) {
    return null;
  }
  return { label: lead.rule.nextTouch.touch.label, channel: lead.rule.nextTouch.touch.channel, scheduledAt: lead.rule.nextTouch.scheduledAt };
}

// -----------------------------------------------------------------------------
// Janela de credito (ADR-001)
// -----------------------------------------------------------------------------

export interface JanelaDeCredito {
  readonly ultimoContatoAt: string;
  readonly canal: ContactChannel;
  readonly touchLabel: string;
  readonly terminaEm: string;
  readonly aberta: boolean;
}

/**
 * Enquanto nao ha pagamento: ate quando uma venda ainda credita comissao.
 * `null` quando nao houve contato (nada abre a janela) ou quando ja houve
 * desfecho de dinheiro (recuperado, pagou sozinho, cancelado).
 */
export function janelaDeCredito(lead: LeadDetail, agora: Date, local: EstadoLocalDoLead = SEM_ESTADO_LOCAL): JanelaDeCredito | null {
  const situacao = situacaoDoLead(lead, local);
  if (!SITUACAO[situacao].emAberto) {
    return null;
  }
  const contatos = contatosQueContam(lead.timeline);
  // ADR-001 E5: so toque do MOTOR abre a janela. A vendedora assumir a conversa
  // nao a reabre -- a comissao continua sendo do ultimo toque.
  const base = contatos[contatos.length - 1];
  if (base === undefined) {
    return null;
  }
  const terminaEm = new Date(new Date(base.at).getTime() + JANELA_DE_CREDITO_DIAS * DIA_MS);
  return { ultimoContatoAt: base.at, canal: base.channel, touchLabel: base.touchLabel, terminaEm: terminaEm.toISOString(), aberta: terminaEm.getTime() > agora.getTime() };
}

// -----------------------------------------------------------------------------
// Plano da regua (o "onde estou" desenhado a partir do fato)
// -----------------------------------------------------------------------------

export type EstadoDoDegrau = 'feito' | 'proximo' | 'agendado' | 'pulado' | 'segurado';

export interface DegrauDoPlano {
  readonly degrau: DegrauDaRegua;
  readonly status: EstadoDoDegrau;
  /** Instante previsto (T0 + atraso) ou, se feito, o instante real do primeiro evento do toque. */
  readonly at: string;
  /** Uma frase: "atendida · 1min 36s", "lido", "pagou antes", "régua encerrada"... */
  readonly nota: string | null;
  /** Toque que aconteceu FORA da regua da empresa (retentativa atrasada, link pedido na ligacao, envio manual). */
  readonly extra: boolean;
}

function notaDoToqueFeito(eventos: readonly TimelineEvent[]): string | null {
  const ligacoes = eventos.filter((e): e is Extract<TimelineEvent, { kind: 'voice_call' }> => e.kind === 'voice_call');
  if (ligacoes.length > 0) {
    return ligacoes.map((l) => `${l.attempt}ª ${DESFECHO_LABEL[l.status]}`).join(' · ');
  }
  // O e-mail de reserva tem estagio proprio: juntar tudo numa lista so fazia o
  // "enviado" do e-mail esconder o "lido" do WhatsApp do mesmo toque.
  let principal: DesfechoDoContato | undefined;
  let reserva: DesfechoDoContato | undefined;
  for (const e of eventos) {
    if (e.kind === 'email' && e.fallback) {
      reserva = e.stage;
    } else if (e.kind === 'whatsapp' || e.kind === 'email') {
      principal = e.stage;
    }
  }
  if (principal === undefined) {
    return reserva === undefined ? null : `e-mail de reserva ${DESFECHO_LABEL[reserva]}`;
  }
  const texto = DESFECHO_LABEL[principal];
  return reserva === undefined ? texto : `${texto} · e-mail de reserva ${DESFECHO_LABEL[reserva]}`;
}

/**
 * Cruza a regua da empresa (`rule.ladder`) com a linha do tempo. Nenhum
 * degrau fica antes de T0: o `scheduledAt` de cada um e fato + atraso.
 */
export function planoDaRegua(lead: LeadDetail, local: EstadoLocalDoLead = SEM_ESTADO_LOCAL): readonly DegrauDoPlano[] {
  const situacao = situacaoDoLead(lead, local);
  const viva = SITUACAO[situacao].viva;
  const fato = new Date(lead.abandonedAt).getTime();
  const proximo = lead.rule.nextTouch;
  const motivoDoPulo: string =
    situacao === 'recuperado'
      ? 'não precisou: já pagou'
      : situacao === 'pagou_sozinho'
        ? !local.vendaMarcada && pagamentoSemCredito(lead).kind === 'antes_do_primeiro_toque'
          ? 'não precisou: pagou antes do 1º toque'
          : 'não precisou: já pagou'
        : situacao === 'com_comercial'
          ? 'régua parou: vendedora assumiu'
          : situacao === 'cancelado'
            ? 'régua cancelada'
            : situacao === 'falhou'
              ? 'régua encerrada pelo motor'
              : 'não enviado';

  const toques = ordenados(lead.timeline).filter((e): e is Extract<TimelineEvent, { kind: 'voice_call' | 'whatsapp' | 'email' }> => e.kind === 'voice_call' || e.kind === 'whatsapp' || e.kind === 'email');
  const rotulosDaRegua = new Set(lead.rule.ladder.map((d) => d.label));

  const plano: DegrauDoPlano[] = lead.rule.ladder.map((degrau) => {
    const eventos = toques.filter((e) => e.touchLabel === degrau.label);
    const previsto = new Date(fato + degrau.delayMinutes * MIN_MS).toISOString();
    if (eventos.length > 0) {
      return { degrau, status: 'feito', at: eventos[0]?.at ?? previsto, nota: notaDoToqueFeito(eventos), extra: false };
    }
    if (proximo !== null && proximo.touch.label === degrau.label) {
      if (local.pausada || lead.rule.paused) {
        return { degrau, status: 'segurado', at: proximo.scheduledAt, nota: 'segurado pela pausa', extra: false };
      }
      return { degrau, status: 'proximo', at: proximo.scheduledAt, nota: null, extra: false };
    }
    if (viva) {
      return { degrau, status: 'agendado', at: previsto, nota: null, extra: false };
    }
    return { degrau, status: 'pulado', at: previsto, nota: motivoDoPulo, extra: false };
  });

  // Toques que aconteceram fora da regua (retentativa atrasada, link pedido na ligacao):
  // entram como "feito · fora da régua", na posicao cronologica, para o plano nunca
  // esconder um contato que existiu -- e que pode ser o creditado.
  const extras = new Map<string, typeof toques>();
  for (const e of toques) {
    if (!rotulosDaRegua.has(e.touchLabel)) {
      extras.set(e.touchLabel, [...(extras.get(e.touchLabel) ?? []), e]);
    }
  }
  let ordem = lead.rule.ladder.length;
  for (const [label, eventos] of extras) {
    const primeiro = eventos[0];
    if (primeiro === undefined) {
      continue;
    }
    ordem += 1;
    const canal: ContactChannel = primeiro.kind === 'voice_call' ? 'voice' : primeiro.kind === 'whatsapp' ? 'whatsapp' : 'email';
    const delayMinutes = Math.max(0, Math.round((new Date(primeiro.at).getTime() - fato) / MIN_MS));
    plano.push({
      degrau: { touchId: `extra-${label}`, label, channel: canal, order: ordem, delayMinutes, isLast: false },
      status: 'feito',
      at: primeiro.at,
      nota: notaDoToqueFeito(eventos),
      extra: true,
    });
  }
  return plano.sort((a, b) => a.degrau.delayMinutes - b.degrau.delayMinutes || a.degrau.order - b.degrau.order);
}

// -----------------------------------------------------------------------------
// Frase executiva
// -----------------------------------------------------------------------------

export interface SegmentoDoResumo {
  readonly key: string;
  /** Texto com o numero em destaque separado, para a tela usar <strong>. */
  readonly antes: string;
  readonly destaque: string;
  readonly depois: string;
}

export interface ResumoExecutivoDoLead {
  readonly segmentos: readonly SegmentoDoResumo[];
  /** A mesma frase em texto corrido (leitor de tela, title, CSV). */
  readonly frase: string;
  readonly situacao: SituacaoDoLead;
  readonly janela: JanelaDeCredito | null;
}

function plural(n: number, singular: string, pluralTexto: string): string {
  return `${n} ${n === 1 ? singular : pluralTexto}`;
}

/**
 * "Abandonou R$ 897,00 há 3 h · recebeu 2 ligações e 1 WhatsApp · último
 * toque há 2 h (WhatsApp T+49min) · pagou há 2 h, creditado ao WhatsApp".
 * Cada segmento parte de um fato ja ocorrido; nao existe "vai vencer".
 */
export function resumoExecutivo(lead: LeadDetail, agora: Date, local: EstadoLocalDoLead = SEM_ESTADO_LOCAL): ResumoExecutivoDoLead {
  const situacao = situacaoDoLead(lead, local);
  const fato = FATO[lead.funnel];
  const segmentos: SegmentoDoResumo[] = [];

  segmentos.push({
    key: 'fato',
    antes: lead.funnel === 'expirados' ? 'Plano de ' : 'Abandonou ',
    destaque: formatBRLFromCents(lead.amountCents),
    depois: `${lead.funnel === 'expirados' ? ' venceu ' : ' '}${descreverRelativo(lead.abandonedAt, agora)}`,
  });

  const toques = toquesRecebidos(lead.timeline);
  if (toques.total === 0) {
    segmentos.push({ key: 'toques', antes: '', destaque: 'ainda sem contato', depois: situacao === 'na_fila' ? ` (na ${fato.filaLabel})` : '' });
  } else {
    const partes: string[] = [];
    if (toques.ligacoes > 0) {
      partes.push(plural(toques.ligacoes, 'ligação', 'ligações'));
    }
    if (toques.whatsapps > 0) {
      partes.push(plural(toques.whatsapps, 'WhatsApp', 'WhatsApps'));
    }
    if (toques.emails > 0) {
      partes.push(plural(toques.emails, 'e-mail', 'e-mails'));
    }
    const texto = partes.length === 1 ? partes[0] ?? '' : `${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1] ?? ''}`;
    segmentos.push({ key: 'toques', antes: 'recebeu ', destaque: texto, depois: '' });
  }

  const ultimo = ultimoContato(lead.timeline);
  if (ultimo !== null) {
    const rotulo = ultimo.outcome === 'replied' || ultimo.outcome === 'took_over' || ultimo.outcome === 'sale' ? DESFECHO_LABEL[ultimo.outcome] : `${CANAL[ultimo.channel].curto}${ultimo.touchLabel === '' ? '' : ` ${ultimo.touchLabel}`} ${DESFECHO_LABEL[ultimo.outcome]}`;
    segmentos.push({ key: 'ultimo', antes: 'último contato ', destaque: descreverRelativo(ultimo.at, agora), depois: ` (${rotulo})` });
  }

  const janela = janelaDeCredito(lead, agora, local);
  const proximo = proximoToque(lead, local);

  if (local.vendaMarcada) {
    segmentos.push({
      key: 'desfecho',
      antes: '',
      destaque: 'venda assistida registrada',
      depois:
        local.vendaDesfecho === 'sem_contato'
          ? ' nesta prévia · sem comissão (sem toque nos 7 dias)'
          : local.vendaDesfecho === 'creditado_revertido'
            ? ' nesta prévia · comissão revertida'
            : ' nesta prévia · comissão creditada ao último toque',
    });
  } else if (situacao === 'recuperado' && lead.conversion !== null) {
    segmentos.push({
      key: 'desfecho',
      antes: 'pagou ',
      destaque: descreverRelativo(lead.conversion.paidAt, agora),
      depois: `, creditado ${aoContato(`${CANAL[lead.conversion.creditedTo.channel].curto} ${lead.conversion.creditedTo.touchLabel}`.trim())} · comissão ${formatBRLFromCents(lead.conversion.commissionCents)}`,
    });
  } else if (situacao === 'pagou_sozinho' && lead.recoveredAt !== null) {
    const motivo = pagamentoSemCredito(lead);
    segmentos.push(
      motivo.kind === 'antes_do_primeiro_toque'
        ? { key: 'desfecho', antes: 'pagou sozinho ', destaque: descreverRelativo(lead.recoveredAt, agora), depois: ', antes do 1º toque · sem comissão' }
        : motivo.kind === 'fora_da_janela'
          ? { key: 'desfecho', antes: 'pagou ', destaque: descreverRelativo(lead.recoveredAt, agora), depois: `, ${motivo.diasDepoisDoUltimo} dias depois do último toque (fora da janela de 7 dias) · sem comissão` }
          : { key: 'desfecho', antes: 'pagou ', destaque: descreverRelativo(lead.recoveredAt, agora), depois: ' · sem comissão registrada' },
    );
  } else if (proximo !== null) {
    segmentos.push({ key: 'proximo', antes: 'próximo toque ', destaque: descreverRelativo(proximo.scheduledAt, agora), depois: ` (${CANAL[proximo.channel].curto} ${proximo.label})` });
  } else if (local.pausada && SITUACAO[situacao].viva) {
    segmentos.push({ key: 'proximo', antes: 'próximo toque ', destaque: 'segurado pela pausa', depois: '' });
  } else if (situacao === 'com_comercial' && janela !== null) {
    segmentos.push({
      key: 'desfecho',
      antes: 'com a vendedora · crédito garantido ',
      destaque: janela.aberta ? `até ${formatDateTime(janela.terminaEm)}` : 'venceu',
      depois: janela.aberta ? ` (${prazoRestante((new Date(janela.terminaEm).getTime() - agora.getTime()) / MIN_MS)} restantes)` : '',
    });
  } else if (situacao === 'esgotado') {
    const fim = lead.timeline.find((e) => e.kind === 'rule_ended');
    segmentos.push({ key: 'desfecho', antes: 'esgotou a régua ', destaque: fim === undefined ? 'sem voltar' : descreverRelativo(fim.at, agora), depois: fim === undefined ? '' : ' sem voltar' });
  } else if (situacao === 'falhou') {
    const falha = lead.timeline.find((e) => e.kind === 'job_failed');
    segmentos.push({ key: 'desfecho', antes: 'régua encerrada pelo motor ', destaque: falha === undefined ? '' : descreverRelativo(falha.at, agora), depois: '' });
  } else if (situacao === 'cancelado') {
    segmentos.push({ key: 'desfecho', antes: '', destaque: 'régua cancelada', depois: '' });
  }

  const frase = segmentos.map((s) => `${s.antes}${s.destaque}${s.depois}`).join(' · ');
  return { segmentos, frase, situacao, janela };
}

// -----------------------------------------------------------------------------
// Sugestoes ("o que fazer agora" do lead)
// -----------------------------------------------------------------------------

export type TomDaSugestao = 'alerta' | 'oportunidade' | 'info' | 'bom';

export type AcaoDaSugestao = { readonly kind: 'marcar_venda' } | { readonly kind: 'rolar'; readonly alvo: string } | { readonly kind: 'rota'; readonly to: string; readonly adminOnly: boolean };

export interface SugestaoDoLead {
  readonly key: string;
  readonly tom: TomDaSugestao;
  readonly titulo: string;
  readonly texto: string;
  readonly acao: AcaoDaSugestao | null;
  readonly rotuloAcao: string | null;
}

/**
 * No maximo tres sugestoes, da mais urgente para a menos. Todas partem do
 * que ja aconteceu com este lead; nenhuma propoe contato antes do fato.
 */
/**
 * O que acontece com a comissao num estorno, pela politica DA EMPRESA
 * (`companies.refund_policy`, ADR-001 E2). O texto fixo da politica padrao
 * mentia para quem escolheu outra: a empresa lia "chargeback reverte" e a
 * cobranca seguia a politica dela, ou o contrario.
 */
export function fraseDaPoliticaDeEstorno(politica: RefundPolicy): string {
  switch (politica) {
    case 'nao_reverte':
      return 'Pela política desta empresa, nem reembolso nem chargeback revertem a comissão.';
    case 'reverte_tudo':
      return 'Pela política desta empresa, reembolso e chargeback revertem a comissão.';
    case 'reverte_chargeback':
      return 'Pela política desta empresa, reembolso não reverte a comissão; chargeback reverte.';
    default: {
      const nunca: never = politica;
      return nunca;
    }
  }
}

export function sugestoesDoLead(lead: LeadDetail, agora: Date, local: EstadoLocalDoLead = SEM_ESTADO_LOCAL, politicaDeEstorno: RefundPolicy = 'reverte_chargeback'): readonly SugestaoDoLead[] {
  const situacao = situacaoDoLead(lead, local);
  const janela = janelaDeCredito(lead, agora, local);
  const lista: SugestaoDoLead[] = [];
  const nome = lead.name === null ? 'Este lead' : (lead.name.split(/\s+/)[0] ?? 'Este lead');
  const valor = formatBRLFromCents(lead.amountCents);
  const ultimo = ultimoContato(lead.timeline);
  const resposta = ordenados(lead.timeline).filter((e) => e.kind === 'lead_reply').at(-1);

  if (local.vendaMarcada) {
    lista.push({
      key: 'venda-marcada',
      tom: local.vendaDesfecho === 'creditado' ? 'bom' : 'info',
      titulo:
        local.vendaDesfecho === 'sem_contato'
          ? 'Venda registrada nesta prévia, sem comissão'
          : local.vendaDesfecho === 'creditado_revertido'
            ? 'Venda registrada nesta prévia, comissão revertida'
            : 'Venda registrada nesta prévia',
      texto:
        local.vendaDesfecho === 'sem_contato'
          ? 'Não houve toque da régua nos 7 dias antes do pagamento: a venda fica no histórico da empresa e não gera comissão.'
          : local.vendaDesfecho === 'creditado_revertido'
            ? 'A venda foi creditada ao último toque e revertida no mesmo ato: já havia estorno desta referência e a política de estorno da empresa o reverte. Fica no histórico, sem comissão a cobrar.'
            : 'Em produção ela entra pelo provedor manual, grava a conversão em recovery_conversions e para a régua. A comissão fica com o último toque do motor; a vendedora aparece como “fechado por”.',
      acao: null,
      rotuloAcao: null,
    });
    return lista;
  }

  if (situacao === 'com_comercial' && janela !== null) {
    const restante = Math.max(0, (new Date(janela.terminaEm).getTime() - agora.getTime()) / MIN_MS);
    lista.push({
      key: 'fechar-venda',
      tom: janela.aberta ? (restante < 2 * 1440 ? 'alerta' : 'oportunidade') : 'alerta',
      titulo: janela.aberta ? `Feche a venda em até ${prazoRestante(restante)} para creditar a comissão` : 'A janela de 7 dias venceu: uma venda agora não credita comissão',
      texto: janela.aberta
        ? `${nome} respondeu${resposta === undefined ? '' : ` ("${resposta.kind === 'lead_reply' ? resposta.text : ''}")`} e está com a vendedora. Pelo ADR-001, a venda paga até ${formatDateTime(janela.terminaEm)} continua creditada ${aoContato(`${CANAL[janela.canal].label}${janela.touchLabel === '' ? '' : ` ${janela.touchLabel}`}`)}; a vendedora fica como “fechado por”. Marque aqui quando sair.`
        : `O último contato foi ${descreverRelativo(janela.ultimoContatoAt, agora)}. Uma venda depois de 7 dias entra como venda normal da empresa, sem comissão.`,
      acao: janela.aberta ? { kind: 'marcar_venda' } : null,
      rotuloAcao: janela.aberta ? 'Marcar venda' : null,
    });
  }

  if ((situacao === 'esgotado' || situacao === 'falhou') && janela !== null && janela.aberta) {
    const restante = (new Date(janela.terminaEm).getTime() - agora.getTime()) / MIN_MS;
    lista.push({
      key: 'contato-humano',
      tom: lead.highValue ? 'oportunidade' : 'info',
      titulo: lead.highValue ? `Alto valor sem resposta: ${valor} ainda dá para recuperar` : `Ainda dá para recuperar ${valor} com um contato humano`,
      texto: `A régua terminou sem ${nome} voltar, mas a janela de crédito segue aberta por ${prazoRestante(restante)} (7 dias após o último contato). Uma mensagem manual da equipe do Converte Fácil ou uma ligação da vendedora que feche a venda até ${formatDateTime(janela.terminaEm)} ainda credita comissão.`,
      acao: { kind: 'rota', to: '/admin/disparos/whatsapp', adminOnly: true },
      rotuloAcao: 'Enviar mensagem manual',
    });
  }

  if (situacao === 'falhou') {
    const falha = lead.timeline.find((e) => e.kind === 'job_failed');
    lista.push({
      key: 'falha-motor',
      tom: 'alerta',
      titulo: 'O motor desistiu deste lead por falha de canal',
      texto: falha === undefined ? 'Confira as integrações: se o WhatsApp estiver fora do ar, todos os leads do Tier 2 param aqui.' : `${falha.kind === 'job_failed' ? falha.lastError : ''} Confira a integração antes que outros leads caiam no mesmo ponto.`,
      acao: { kind: 'rota', to: '/configuracoes', adminOnly: false },
      rotuloAcao: 'Ver integrações',
    });
  }

  if (situacao === 'na_fila') {
    const proximo = proximoToque(lead, local);
    lista.push({
      key: 'na-fila',
      tom: 'info',
      titulo: proximo === null ? `${nome} está na ${FATO[lead.funnel].filaLabel}` : `Primeiro toque ${descreverRelativo(proximo.scheduledAt, agora)}: nada a fazer`,
      texto: `A espera existe de propósito: quem paga sozinho nesse intervalo sai da régua sem custo e sem comissão. Só depois dela o motor liga${lead.funnel === 'expirados' ? ' ou manda mensagem' : ''}.`,
      acao: null,
      rotuloAcao: null,
    });
  }

  if (situacao === 'em_recuperacao') {
    const proximo = proximoToque(lead, local);
    const leu = ultimo !== null && (ultimo.outcome === 'read' || ultimo.outcome === 'clicked');
    lista.push({
      key: 'em-regua',
      tom: leu ? 'oportunidade' : 'info',
      titulo: leu ? `${nome} ${ultimo.outcome === 'clicked' ? 'clicou no botão' : 'leu a mensagem'} e ainda não pagou` : proximo === null ? 'A régua está andando' : `Próximo toque ${descreverRelativo(proximo.scheduledAt, agora)} (${CANAL[proximo.channel].label} ${proximo.label})`,
      texto: leu
        ? 'Sinal quente: quem lê ou clica e não paga costuma ter travado no pagamento. O próximo toque da régua já fala disso; se preferir, pause a régua e deixe a vendedora falar.'
        : 'O motor cuida sozinho. Pausar só faz sentido se alguém da equipe for falar com a pessoa por fora.',
      acao: leu ? { kind: 'rolar', alvo: 'regua-do-lead' } : null,
      rotuloAcao: leu ? 'Ver a régua' : null,
    });
  }

  if (situacao === 'pagou_sozinho') {
    const motivo = pagamentoSemCredito(lead);
    lista.push(
      motivo.kind === 'antes_do_primeiro_toque'
        ? {
            key: 'pagou-sozinho',
            tom: 'bom',
            titulo: `${nome} pagou antes do primeiro toque`,
            texto: 'Nenhum toque foi enviado e não há comissão: a régua só cobra sobre quem já tinha saído. É exatamente o comportamento esperado.',
            acao: null,
            rotuloAcao: null,
          }
        : motivo.kind === 'fora_da_janela'
          ? {
              key: 'pagou-sozinho',
              tom: 'info',
              titulo: `${nome} pagou depois da janela de 7 dias`,
              texto: `Recebeu toques da régua, mas pagou ${motivo.diasDepoisDoUltimo} dias depois do último contato. Pela regra da comissão, o crédito vale por 7 dias: esta venda é da empresa e não gera comissão.`,
              acao: null,
              rotuloAcao: null,
            }
          : {
              key: 'pagou-sozinho',
              tom: 'info',
              titulo: `${nome} pagou, sem comissão registrada`,
              texto: 'Recebeu toques da régua dentro dos 7 dias, mas o motor não registrou crédito para este pagamento. Se a venda veio da régua, confira com a administração do Converte Fácil.',
              acao: null,
              rotuloAcao: null,
            },
    );
  }

  if (situacao === 'recuperado' && lead.conversion !== null) {
    lista.push({
      key: 'recuperado',
      tom: 'bom',
      titulo: `Recuperado: ${formatBRLFromCents(lead.conversion.amountCents)} pagos, comissão ${formatBRLFromCents(lead.conversion.commissionCents)}`,
      texto: `Crédito ${aoContato(`${CANAL[lead.conversion.creditedTo.channel].label} ${lead.conversion.creditedTo.touchLabel}`.trim())} (último contato antes do pagamento). ${fraseDaPoliticaDeEstorno(politicaDeEstorno)}`,
      acao: null,
      rotuloAcao: null,
    });
  }

  return lista.slice(0, 3);
}

/** Icone/rotulo do funil para textos curtos. */
export function funilCurto(funnel: FunnelKind): string {
  return funnel === 'expirados' ? 'plano vencido' : 'carrinho abandonado';
}
